import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import DocumentManager from '../components/DocumentManager';
import InvestmentNotes from '../components/InvestmentNotes';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Upload,
  Loader2,
  ExternalLink,
  FileText,
  Eye,
  Download,
  FileSpreadsheet,
  MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTORS = ['Payments', 'Identity', 'Credit', 'Insurance', 'AI/Data', 'Healthcare', 'Other'];
const STAGES = ['Pre-seed', 'Seed', 'Series A', 'Series B', 'Growth'];
const DEAL_STAGES = ['sourcing', 'due_diligence', 'term_sheet', 'closed', 'active', 'exit'];

const formatCurrency = (value) => {
  if (!value) return '-';
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const StageLabel = ({ stage }) => {
  const stageClass = `stage-${stage.replace('_', '-')}`;
  return (
    <span className={`stage-badge ${stageClass}`}>
      {stage.replace('_', ' ')}
    </span>
  );
};

const emptyInvestment = {
  company_name: '',
  sector: '',
  stage: '',
  investment_amount: '',
  ownership_percentage: '',
  investment_date: '',
  current_valuation: '',
  description: '',
  location: '',
  founders: '',
  website: '',
  deal_stage: 'active',
  notes: ''
};

export default function PortfolioPage() {
  const { getAuthHeaders, user } = useAuth();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [formData, setFormData] = useState(emptyInvestment);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const canEdit = ['admin', 'team_member'].includes(user?.role);

  useEffect(() => {
    fetchInvestments();
  }, []);

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/investments`, {
        headers: getAuthHeaders()
      });
      setInvestments(response.data);
    } catch (error) {
      toast.error('Failed to load investments');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (investment = null) => {
    if (investment) {
      setEditingInvestment(investment);
      setFormData({
        company_name: investment.company_name || '',
        sector: investment.sector || '',
        stage: investment.stage || '',
        investment_amount: investment.investment_amount?.toString() || '',
        ownership_percentage: investment.ownership_percentage?.toString() || '',
        investment_date: investment.investment_date || '',
        current_valuation: investment.current_valuation?.toString() || '',
        description: investment.description || '',
        location: investment.location || '',
        founders: investment.founders || '',
        website: investment.website || '',
        deal_stage: investment.deal_stage || 'active',
        notes: investment.notes || ''
      });
    } else {
      setEditingInvestment(null);
      setFormData(emptyInvestment);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.company_name || !formData.sector || !formData.stage) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        investment_amount: parseFloat(formData.investment_amount) || 0,
        ownership_percentage: parseFloat(formData.ownership_percentage) || 0,
        current_valuation: formData.current_valuation ? parseFloat(formData.current_valuation) : null
      };

      if (editingInvestment) {
        await axios.put(
          `${API_URL}/api/investments/${editingInvestment.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Investment updated');
      } else {
        await axios.post(
          `${API_URL}/api/investments`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Investment added');
      }
      
      setDialogOpen(false);
      fetchInvestments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save investment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (investment) => {
    if (!window.confirm(`Delete ${investment.company_name}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/investments/${investment.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Investment deleted');
      fetchInvestments();
    } catch (error) {
      toast.error('Failed to delete investment');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/import/investments`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        console.warn('Import errors:', response.data.errors);
      }
      setImportDialogOpen(false);
      fetchInvestments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const filteredInvestments = investments.filter(inv => {
    const matchesSearch = inv.company_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSector = sectorFilter === 'all' || inv.sector === sectorFilter;
    return matchesSearch && matchesSector;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="portfolio-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Portfolio</h1>
          <p className="text-slate-500 mt-1">{investments.length} investments</p>
        </div>
        <div className="flex gap-3">
          {/* Export Buttons */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-btn">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => window.open(`${API_URL}/api/investments/export/excel`, '_blank')}
                className="cursor-pointer"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />
                Export to Excel
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open(`${API_URL}/api/investments/export/pdf`, '_blank')}
                className="cursor-pointer"
              >
                <FileText className="h-4 w-4 mr-2 text-red-600" />
                Export to PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {canEdit && (
            <>
              <Button 
                variant="outline" 
                onClick={() => setImportDialogOpen(true)}
                data-testid="import-csv-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </Button>
              <Button 
                className="btn-primary" 
                onClick={() => handleOpenDialog()}
                data-testid="add-investment-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Investment
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger className="w-[180px]" data-testid="sector-filter">
                <SelectValue placeholder="All Sectors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {SECTORS.map(sector => (
                  <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card data-testid="investments-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-widest">Company</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Sector</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Stage</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-right">Invested</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-right">Ownership</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-right">Valuation</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-right">MOIC</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                {canEdit && <TableHead className="w-10"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvestments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 9 : 8} className="text-center py-8 text-slate-400">
                    No investments found
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvestments.map((inv) => (
                  <TableRow key={inv.id} data-testid={`investment-row-${inv.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{inv.company_name}</span>
                        {inv.website && (
                          <a 
                            href={inv.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-slate-400 hover:text-[#1E3A5F]"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                      {inv.location && (
                        <p className="text-xs text-slate-400">{inv.location}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{inv.sector}</Badge>
                    </TableCell>
                    <TableCell className="text-slate-600">{inv.stage}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(inv.investment_amount)}</TableCell>
                    <TableCell className="text-right font-mono">{inv.ownership_percentage}%</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(inv.current_valuation)}</TableCell>
                    <TableCell className="text-right">
                      {inv.moic ? (
                        <span className={`font-mono font-medium ${inv.moic >= 1 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {inv.moic}x
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <StageLabel stage={inv.deal_stage} />
                    </TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <InvestmentNotes 
                            investmentId={inv.id} 
                            investmentName={inv.company_name}
                          />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`actions-${inv.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setSelectedInvestment(inv);
                                setDocumentsDialogOpen(true);
                              }}>
                                <FileText className="h-4 w-4 mr-2" />
                                Documents
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleOpenDialog(inv)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDelete(inv)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="investment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingInvestment ? 'Edit Investment' : 'Add New Investment'}
            </DialogTitle>
            <DialogDescription>
              {editingInvestment ? 'Update the investment details' : 'Enter the details of the new investment'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                placeholder="OnePay"
                data-testid="input-company-name"
              />
            </div>
            
            <div>
              <Label htmlFor="sector">Sector *</Label>
              <Select 
                value={formData.sector} 
                onValueChange={(value) => setFormData({...formData, sector: value})}
              >
                <SelectTrigger data-testid="select-sector">
                  <SelectValue placeholder="Select sector" />
                </SelectTrigger>
                <SelectContent>
                  {SECTORS.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="stage">Investment Stage *</Label>
              <Select 
                value={formData.stage} 
                onValueChange={(value) => setFormData({...formData, stage: value})}
              >
                <SelectTrigger data-testid="select-stage">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="investment_amount">Investment Amount ($)</Label>
              <Input
                id="investment_amount"
                type="number"
                value={formData.investment_amount}
                onChange={(e) => setFormData({...formData, investment_amount: e.target.value})}
                placeholder="500000"
                data-testid="input-investment-amount"
              />
            </div>

            <div>
              <Label htmlFor="ownership_percentage">Ownership (%)</Label>
              <Input
                id="ownership_percentage"
                type="number"
                step="0.1"
                value={formData.ownership_percentage}
                onChange={(e) => setFormData({...formData, ownership_percentage: e.target.value})}
                placeholder="25"
                data-testid="input-ownership"
              />
            </div>

            <div>
              <Label htmlFor="investment_date">Investment Date</Label>
              <Input
                id="investment_date"
                type="date"
                value={formData.investment_date}
                onChange={(e) => setFormData({...formData, investment_date: e.target.value})}
                data-testid="input-date"
              />
            </div>

            <div>
              <Label htmlFor="current_valuation">Current Valuation ($)</Label>
              <Input
                id="current_valuation"
                type="number"
                value={formData.current_valuation}
                onChange={(e) => setFormData({...formData, current_valuation: e.target.value})}
                placeholder="4000000"
                data-testid="input-valuation"
              />
            </div>

            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="Lagos, Nigeria"
                data-testid="input-location"
              />
            </div>

            <div>
              <Label htmlFor="deal_stage">Deal Status</Label>
              <Select 
                value={formData.deal_stage} 
                onValueChange={(value) => setFormData({...formData, deal_stage: value})}
              >
                <SelectTrigger data-testid="select-deal-stage">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {DEAL_STAGES.map(s => (
                    <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="founders">Founders</Label>
              <Input
                id="founders"
                value={formData.founders}
                onChange={(e) => setFormData({...formData, founders: e.target.value})}
                placeholder="John Doe, Jane Smith"
                data-testid="input-founders"
              />
            </div>

            <div>
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                placeholder="https://onepay.africa"
                data-testid="input-website"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the company..."
                rows={2}
                data-testid="input-description"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Internal notes..."
                rows={2}
                data-testid="input-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-primary" 
              onClick={handleSave}
              disabled={saving}
              data-testid="save-investment-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingInvestment ? 'Update' : 'Add'} Investment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Import Investments from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with the following columns: company_name, sector, stage, investment_amount, ownership_percentage, investment_date, current_valuation, description, location, founders, website, deal_stage, notes
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImport}
              disabled={importing}
              data-testid="csv-file-input"
            />
          </div>

          {importing && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-[#1E3A5F]" />
              <span className="ml-2 text-slate-600">Importing...</span>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
        <DialogContent className="max-w-xl" data-testid="documents-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Documents - {selectedInvestment?.company_name}
            </DialogTitle>
            <DialogDescription>
              Upload and manage documents for this investment
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {selectedInvestment && (
              <DocumentManager 
                investmentId={selectedInvestment.id}
                investmentName={selectedInvestment.company_name}
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
